import type {
  ICaptureUserDetailsActionData,
  IReviewAppointmentActionData,
} from '../types/ChatAgentResponse.types';

interface IChatCopyValidationRule {
  regEx: string;
  message: string;
}

interface IChatCopySelectOption {
  label: string;
  value: string;
}

interface IChatCopyFieldDefinition {
  fieldName: string;
  label: string;
  type: 'input' | 'date' | 'select';
  validations?: IChatCopyValidationRule[];
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  options?: {
    items?: IChatCopySelectOption[];
  };
  items?: IChatCopySelectOption[];
}

const DEFAULT_USER_PREFERENCE_TITLE = 'Provide your details';
const DEFAULT_USER_PREFERENCE_CANCEL_BUTTON = 'Cancel';
const DEFAULT_USER_PREFERENCE_SAVE_BUTTON = 'Save';

export interface IChatCopyData {
  appointmentBookingForm?: {
    title?: string;
    cancelButtonTitle?: string;
    submitButtonTitle?: string;
    fields?: IChatCopyFieldDefinition[];
  };
  footer?: {
    inputPlaceholder?: string;
    disclaimer?: string;
  };
}

export type IDynamicFormFieldDefinition = Omit<IChatCopyFieldDefinition, 'fieldName'>;

export interface IDynamicFormConfig {
  title: string;
  cancelButton: string;
  saveButton: string;
  formFields: Record<string, IDynamicFormFieldDefinition>;
  initialValues?: Partial<Record<string, string>>;
}

interface IDynamicSelectOptions {
  [fieldName: string]: {
    items?: Array<{ label: string; value: string }>;
  };
}

export const mapCaptureUserDetailsToDynamicFormConfig = (
  actionData: ICaptureUserDetailsActionData,
  chatCopy?: IChatCopyData | null,
  dynamicOptions?: IDynamicSelectOptions,
): IDynamicFormConfig => {
  const sourceFields = chatCopy?.appointmentBookingForm?.fields ?? [];

  const formFields = actionData.fields.reduce<Record<string, IDynamicFormFieldDefinition>>(
    (accumulator, fieldName) => {
      const matchingField = sourceFields.find(
        (field: IChatCopyFieldDefinition) => field.fieldName === fieldName,
      );

      if (!matchingField) {
        return accumulator;
      }

      const { fieldName: _fieldName, ...fieldDefinition } = matchingField;

      // Merge dynamic options for select fields
      if (fieldDefinition.type === 'select' && dynamicOptions?.[fieldName]) {
        accumulator[fieldName] = {
          ...fieldDefinition,
          options: dynamicOptions[fieldName],
        };
      } else {
        accumulator[fieldName] = fieldDefinition;
      }

      return accumulator;
    },
    {},
  );

  return {
    title: chatCopy?.appointmentBookingForm?.title ?? DEFAULT_USER_PREFERENCE_TITLE,
    cancelButton:
      chatCopy?.appointmentBookingForm?.cancelButtonTitle ?? DEFAULT_USER_PREFERENCE_CANCEL_BUTTON,
    saveButton:
      chatCopy?.appointmentBookingForm?.submitButtonTitle ?? DEFAULT_USER_PREFERENCE_SAVE_BUTTON,
    formFields,
  };
};

export const mapAppointmentDetailsToProps = (
  actionData: IReviewAppointmentActionData | undefined,
) => {
  if (!actionData) {
    return undefined;
  }

  const { appointmentDetails } = actionData;

  return {
    reason: 'Consultation', // This is not available in appointmentDetails, using a placeholder value. Update this when the data model is finalized.
    provider: appointmentDetails?.providerName ?? '',
    visit: appointmentDetails?.visitType ?? '',
    when: {
      date: appointmentDetails?.date ?? '',
      time: appointmentDetails?.time ?? '',
    },
  };
};
